import { useEffect, useRef } from "react";
import {
  DEFAULT_SHARE_THEME,
  type ShareTheme,
} from "@xiaotian-vault/shared";

type BackdropVariant = "default" | "public";
type ThreeModule = typeof import("three");
type Disposable = { dispose: () => void };

type Particle = {
  x: number;
  y: number;
  z: number;
  seed: number;
  size: number;
  drift: number;
  phase: number;
};

type Connection = {
  a: number;
  b: number;
};

type Pulse = {
  connectionIndex: number;
  seed: number;
  speed: number;
  offset: number;
};

type NodeSprite = {
  sprite: import("three").Sprite;
  scale: number;
  seed: number;
};

type BackdropConfig = {
  particleCounts: [mobile: number, tablet: number, desktop: number];
  pulseCounts: [mobile: number, tablet: number, desktop: number];
  connectionDistance: [mobile: number, desktop: number];
  connectionLimitMultiplier: number;
  connectionOpacity: [base: number, swing: number];
  particleSize: [min: number, range: number];
  pulseSize: [min: number, range: number];
  spriteOpacity: number;
  spriteScale: [base: number, sizeMultiplier: number];
  fieldColors: [primary: string, secondary: string, tertiary: string];
  lineColor: number;
  spriteColor: number;
  spriteStops: Array<[offset: number, color: string]>;
  cyber: boolean;
};

const cinematicStops: Array<[number, string]> = [
  [0, "rgba(255, 252, 229, 1)"],
  [0.18, "rgba(255, 233, 164, 0.9)"],
  [0.48, "rgba(244, 195, 95, 0.32)"],
  [0.74, "rgba(144, 87, 26, 0.12)"],
  [1, "rgba(245, 200, 106, 0)"],
];

const cyberStops: Array<[number, string]> = [
  [0, "rgba(232, 255, 250, 1)"],
  [0.24, "rgba(111, 231, 255, 0.82)"],
  [0.52, "rgba(132, 255, 218, 0.34)"],
  [0.78, "rgba(116, 92, 255, 0.16)"],
  [1, "rgba(66, 217, 255, 0)"],
];

const backdropConfigs: Record<
  ShareTheme,
  Record<BackdropVariant, BackdropConfig>
> = {
  "cinematic-vault": {
    default: {
      particleCounts: [38, 56, 80],
      pulseCounts: [5, 7, 10],
      connectionDistance: [1.34, 1.18],
      connectionLimitMultiplier: 1.25,
      connectionOpacity: [0.085, 0.022],
      particleSize: [8, 12],
      pulseSize: [7, 4],
      spriteOpacity: 0.34,
      spriteScale: [0.025, 0.0019],
      fieldColors: ["#f5c86a", "#fff0bd", "#8c6428"],
      lineColor: 0xf4c35f,
      spriteColor: 0xfff0bd,
      spriteStops: cinematicStops,
      cyber: false,
    },
    public: {
      particleCounts: [48, 72, 106],
      pulseCounts: [7, 10, 15],
      connectionDistance: [1.5, 1.28],
      connectionLimitMultiplier: 1.25,
      connectionOpacity: [0.11, 0.028],
      particleSize: [10, 15],
      pulseSize: [9, 5],
      spriteOpacity: 0.46,
      spriteScale: [0.029, 0.0022],
      fieldColors: ["#f4c35f", "#fff2c6", "#8c511c"],
      lineColor: 0xf4c35f,
      spriteColor: 0xfff2c6,
      spriteStops: cinematicStops,
      cyber: false,
    },
  },
  "cyber-transfer": {
    default: {
      particleCounts: [46, 68, 96],
      pulseCounts: [8, 12, 18],
      connectionDistance: [0.92, 0.78],
      connectionLimitMultiplier: 1.85,
      connectionOpacity: [0.13, 0.045],
      particleSize: [6, 7],
      pulseSize: [7, 6],
      spriteOpacity: 0.42,
      spriteScale: [0.017, 0.0012],
      fieldColors: ["#58e7ff", "#a7fff0", "#745cff"],
      lineColor: 0x58e7ff,
      spriteColor: 0xd8fff7,
      spriteStops: cyberStops,
      cyber: true,
    },
    public: {
      particleCounts: [52, 78, 116],
      pulseCounts: [16, 24, 34],
      connectionDistance: [0.92, 0.78],
      connectionLimitMultiplier: 2.15,
      connectionOpacity: [0.18, 0.06],
      particleSize: [6, 6],
      pulseSize: [9, 8],
      spriteOpacity: 0.55,
      spriteScale: [0.016, 0.00125],
      fieldColors: ["#58e7ff", "#a7fff0", "#745cff"],
      lineColor: 0x58e7ff,
      spriteColor: 0xd8fff7,
      spriteStops: cyberStops,
      cyber: true,
    },
  },
};

const fieldVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const fieldFragmentShader = `
  precision highp float;

  uniform float uTime;
  uniform vec2 uPointer;
  uniform vec2 uResolution;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform vec3 uColorC;
  uniform float uCyber;
  varying vec2 vUv;

  float strand(vec2 p, float angle, float shift, float scale, float width) {
    vec2 q = vec2(
      p.x * cos(angle) - p.y * sin(angle),
      p.x * sin(angle) + p.y * cos(angle)
    );
    float line = 1.0 - smoothstep(0.0, width, abs(fract((q.y + shift) * scale) - 0.5));
    float mask = smoothstep(-1.15, -0.1, q.x) * (1.0 - smoothstep(0.28, 1.1, q.x));
    return line * mask;
  }

  float glint(vec2 p, vec2 center, float angle) {
    vec2 q = p - center;
    vec2 r = vec2(
      q.x * cos(angle) - q.y * sin(angle),
      q.x * sin(angle) + q.y * cos(angle)
    );
    float beam = 1.0 - smoothstep(0.0, 0.012, abs(r.y));
    float falloff = smoothstep(0.36, 0.0, abs(r.x));
    return beam * falloff;
  }

  float cyberLane(float coord, float count, float width) {
    float cell = abs(fract(coord * count) - 0.5);
    return 1.0 - smoothstep(0.0, width, cell);
  }

  float cyberPacket(vec2 uv, float speed, float laneCount, float phase) {
    float lane = cyberLane(uv.x + phase, laneCount, 0.035);
    float head = abs(fract(uv.y * 4.4 - uTime * speed + phase) - 0.5);
    float packet = 1.0 - smoothstep(0.0, 0.06, head);
    return lane * packet;
  }

  void main() {
    float aspect = uResolution.x / max(uResolution.y, 1.0);
    vec2 p = (vUv - 0.5) * vec2(aspect, 1.0);
    vec2 pointer = (uPointer - 0.5) * vec2(aspect, 1.0);

    float slow = uTime * 0.028;
    float lattice =
      strand(p, 0.53, slow, 5.8, 0.012) * 0.1 +
      strand(p, -0.41, -slow * 0.72, 4.6, 0.011) * 0.07;
    vec2 cyberUv = vec2(vUv.x * aspect, vUv.y);
    float verticalGrid =
      cyberLane(cyberUv.x + sin(uTime * 0.08) * 0.025, 15.0, 0.016) * 0.1 +
      cyberLane(cyberUv.x + 0.04, 31.0, 0.012) * 0.035;
    float horizontalGrid =
      cyberLane(vUv.y + uTime * 0.01, 18.0, 0.018) * 0.055 +
      cyberLane(vUv.y - 0.03, 43.0, 0.01) * 0.026;
    float packets =
      cyberPacket(vUv, 0.44, 7.0, 0.03) * 0.36 +
      cyberPacket(vec2(1.0 - vUv.x, vUv.y), 0.68, 9.0, 0.27) * 0.24 +
      cyberPacket(vec2(vUv.x + sin(uTime * 0.18) * 0.02, 1.0 - vUv.y), -0.52, 5.0, 0.61) * 0.22;
    float gate =
      (1.0 - smoothstep(0.0, 0.014, abs(fract((vUv.x + vUv.y * 0.14 + uTime * 0.035) * 8.0) - 0.5))) *
      smoothstep(0.18, 0.42, vUv.x) *
      (1.0 - smoothstep(0.58, 0.9, vUv.x)) * 0.055;
    float scan = (1.0 - smoothstep(0.0, 0.014, abs(fract((vUv.y + uTime * 0.09) * 72.0) - 0.5))) * 0.028;
    float dataGrid = verticalGrid + horizontalGrid + gate;

    vec2 c1 = vec2(sin(uTime * 0.12) * 0.48 - 0.28, cos(uTime * 0.08) * 0.18 + 0.1);
    vec2 c2 = vec2(cos(uTime * 0.09) * 0.54 + 0.18, sin(uTime * 0.11) * 0.22 - 0.18);
    float sheen =
      glint(p, c1, 0.62 + sin(uTime * 0.08) * 0.12) * 0.12 +
      glint(p, c2, -0.42 + cos(uTime * 0.1) * 0.1) * 0.08;

    float pointerWake = 1.0 - smoothstep(0.0, 0.58, distance(p, pointer));
    float vignette = smoothstep(1.08, 0.18, length(p));

    float cyberGrid = (dataGrid + scan) * uCyber;
    float cyberPackets = packets * uCyber;
    vec3 color = uColorA * lattice + uColorB * sheen + uColorC * pointerWake * 0.05;
    color += mix(uColorA, uColorB, 0.38) * pointerWake * 0.05;
    color += mix(uColorA, uColorB, 0.48) * cyberGrid;
    color += mix(uColorB, uColorC, 0.3) * cyberPackets;
    color += uColorC * gate * uCyber * 0.85;

    float alpha = clamp((lattice + sheen + pointerWake * 0.045 + cyberGrid * 0.72 + cyberPackets * 0.52) * vignette, 0.0, 0.48);
    gl_FragColor = vec4(color, alpha);
  }
`;

const particleVertexShader = `
  attribute float aSeed;
  attribute float aSize;
  varying float vSeed;
  varying float vDepth;

  void main() {
    vec4 modelPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * modelPosition;
    float depthScale = 1.12 / max(0.5, -modelPosition.z * 0.18);
    gl_PointSize = aSize * depthScale;
    vSeed = aSeed;
    vDepth = depthScale;
  }
`;

const particleFragmentShader = `
  precision highp float;

  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform float uCyber;
  varying float vSeed;
  varying float vDepth;

  void main() {
    vec2 p = gl_PointCoord - 0.5;
    float dist = length(p);
    float roundHalo = smoothstep(0.5, 0.0, dist);
    float roundCore = smoothstep(0.2, 0.0, dist);
    float squareDistance = max(abs(p.x * 1.18), abs(p.y * 0.82));
    float diamond = abs(p.x) + abs(p.y);
    float dataShard = smoothstep(0.46, 0.2, squareDistance);
    float dataCore = smoothstep(0.22, 0.06, diamond);
    float circuitCross =
      smoothstep(0.035, 0.0, abs(p.x)) * smoothstep(0.48, 0.08, abs(p.y)) +
      smoothstep(0.035, 0.0, abs(p.y)) * smoothstep(0.48, 0.08, abs(p.x));
    float halo = mix(roundHalo, max(dataShard, circuitCross * 0.56), uCyber);
    float core = mix(roundCore, max(dataCore, circuitCross * 0.72), uCyber);
    if (halo < 0.006) discard;

    float shimmer = 0.9 + sin(vSeed * 31.0) * 0.16 + uCyber * sin(vSeed * 71.0) * 0.18;
    float cyberEdge = uCyber * smoothstep(0.34, 0.05, abs(p.x - p.y) * 0.6);
    vec3 color = mix(uColorA, uColorB, core * 0.68 + vDepth * 0.08 + cyberEdge * 0.22) * (0.92 + uCyber * 0.18);
    float alpha = clamp((halo * 0.46 + core * 0.66 + cyberEdge * 0.11) * shimmer, 0.0, 0.76);
    gl_FragColor = vec4(color, alpha);
  }
`;

function seededValue(seed: number): number {
  const value = Math.sin(seed * 91.345) * 47453.5453;
  return value - Math.floor(value);
}

function wrapUnit(value: number): number {
  return value - Math.floor(value);
}

function countFor(
  width: number,
  mobile: number,
  tablet: number,
  desktop: number,
): number {
  if (width < 640) return mobile;
  if (width < 1024) return tablet;
  return desktop;
}

export function DynamicAmbientBackdrop({
  variant = "default",
  theme = DEFAULT_SHARE_THEME,
}: {
  variant?: BackdropVariant;
  theme?: ShareTheme;
}) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const config = backdropConfigs[theme][variant];
    const mount = mountRef.current;
    const canvas = canvasRef.current;
    if (!mount || !canvas) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduceMotion.matches) {
      mount.dataset.backdropStatus = "reduced-motion";
      return;
    }

    let disposed = false;
    let cleanupScene: (() => void) | undefined;
    mount.dataset.backdropStatus = "loading";

    const startScene = async () => {
      const three = await import("three");
      if (disposed || !mount.isConnected) return;

      const {
        BufferGeometry,
        CanvasTexture,
        Color,
        Float32BufferAttribute,
        LineBasicMaterial,
        LineSegments,
        OrthographicCamera,
        PerspectiveCamera,
        PlaneGeometry,
        Points,
        Scene,
        ShaderMaterial,
        Sprite,
        SpriteMaterial,
        Vector2,
        WebGLRenderer,
      } = three;

      let frame = 0;
      let width = window.innerWidth;
      let height = window.innerHeight;
      const pointer = new Vector2(0.5, 0.5);
      const targetPointer = new Vector2(0.5, 0.5);
      let hoverTarget = 0;
      let hoverStrength = 0;
      const geometries: Disposable[] = [];
      const materials: Disposable[] = [];
      const textures: Disposable[] = [];

      const renderer = new WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        powerPreference: "high-performance",
      });
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.65));
      renderer.setSize(width, height, false);

      const fieldScene = new Scene();
      const fieldCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
      const scene = new Scene();
      const camera = new PerspectiveCamera(
        42,
        width / Math.max(height, 1),
        0.1,
        60,
      );
      camera.position.set(0, 0, 7.4);

      const fieldGeometry = new PlaneGeometry(2, 2, 1, 1);
      const fieldUniforms = {
        uTime: { value: 0 },
        uPointer: { value: pointer },
        uResolution: { value: new Vector2(width, height) },
        uColorA: { value: new Color(config.fieldColors[0]) },
        uColorB: { value: new Color(config.fieldColors[1]) },
        uColorC: { value: new Color(config.fieldColors[2]) },
        uCyber: { value: config.cyber ? 1 : 0 },
      };
      const fieldMaterial = new ShaderMaterial({
        transparent: true,
        depthWrite: false,
        depthTest: false,
        uniforms: fieldUniforms,
        vertexShader: fieldVertexShader,
        fragmentShader: fieldFragmentShader,
      });
      geometries.push(fieldGeometry);
      materials.push(fieldMaterial);
      fieldScene.add(new three.Mesh(fieldGeometry, fieldMaterial));

      const particles = createParticles(
        countFor(width, ...config.particleCounts),
        config,
      );
      const particlePositions = new Float32Array(particles.length * 3);
      const particleSeeds = new Float32Array(particles.length);
      const particleSizes = new Float32Array(particles.length);
      for (let index = 0; index < particles.length; index += 1) {
        const particle = particles[index];
        if (!particle) continue;
        particleSeeds[index] = particle.seed;
        particleSizes[index] = particle.size;
      }

      const particleGeometry = new BufferGeometry();
      const particlePositionAttribute = new Float32BufferAttribute(
        particlePositions,
        3,
      );
      particleGeometry.setAttribute("position", particlePositionAttribute);
      particleGeometry.setAttribute(
        "aSeed",
        new Float32BufferAttribute(particleSeeds, 1),
      );
      particleGeometry.setAttribute(
        "aSize",
        new Float32BufferAttribute(particleSizes, 1),
      );
      const particleMaterial = new ShaderMaterial({
        transparent: true,
        depthWrite: false,
        depthTest: false,
        blending: three.AdditiveBlending,
        uniforms: {
          uColorA: { value: new Color(config.fieldColors[0]) },
          uColorB: { value: new Color(config.fieldColors[1]) },
          uCyber: { value: config.cyber ? 1 : 0 },
        },
        vertexShader: particleVertexShader,
        fragmentShader: particleFragmentShader,
      });
      geometries.push(particleGeometry);
      materials.push(particleMaterial);
      scene.add(new Points(particleGeometry, particleMaterial));

      const nodeSprites = createNodeSprites({
        CanvasTexture,
        Sprite,
        SpriteMaterial,
        particles,
        config,
        materials,
        textures,
        additiveBlending: three.AdditiveBlending,
      });
      for (const node of nodeSprites) scene.add(node.sprite);

      const connections = createConnections(
        particles,
        width < 640
          ? config.connectionDistance[0]
          : config.connectionDistance[1],
        config,
      );
      const connectionPositions = new Float32Array(connections.length * 6);
      const connectionGeometry = new BufferGeometry();
      const connectionPositionAttribute = new Float32BufferAttribute(
        connectionPositions,
        3,
      );
      connectionGeometry.setAttribute("position", connectionPositionAttribute);
      const connectionMaterial = new LineBasicMaterial({
        color: config.lineColor,
        transparent: true,
        opacity: config.connectionOpacity[0],
        blending: three.AdditiveBlending,
        depthWrite: false,
      });
      geometries.push(connectionGeometry);
      materials.push(connectionMaterial);
      scene.add(new LineSegments(connectionGeometry, connectionMaterial));

      const pulses = createPulses(
        connections,
        countFor(width, ...config.pulseCounts),
        config,
      );
      const pulsePositions = new Float32Array(pulses.length * 3);
      const pulseSeeds = new Float32Array(pulses.length);
      const pulseSizes = new Float32Array(pulses.length);
      for (let index = 0; index < pulses.length; index += 1) {
        const pulse = pulses[index];
        if (!pulse) continue;
        pulseSeeds[index] = pulse.seed;
        pulseSizes[index] =
          config.pulseSize[0] +
          seededValue(pulse.seed + 8) * config.pulseSize[1];
      }
      const pulseGeometry = new BufferGeometry();
      const pulsePositionAttribute = new Float32BufferAttribute(
        pulsePositions,
        3,
      );
      pulseGeometry.setAttribute("position", pulsePositionAttribute);
      pulseGeometry.setAttribute(
        "aSeed",
        new Float32BufferAttribute(pulseSeeds, 1),
      );
      pulseGeometry.setAttribute(
        "aSize",
        new Float32BufferAttribute(pulseSizes, 1),
      );
      const pulseMaterial = new ShaderMaterial({
        transparent: true,
        depthWrite: false,
        depthTest: false,
        blending: three.AdditiveBlending,
        uniforms: {
          uColorA: { value: new Color(config.fieldColors[0]) },
          uColorB: { value: new Color(config.fieldColors[1]) },
          uCyber: { value: config.cyber ? 1 : 0 },
        },
        vertexShader: particleVertexShader,
        fragmentShader: particleFragmentShader,
      });
      geometries.push(pulseGeometry);
      materials.push(pulseMaterial);
      scene.add(new Points(pulseGeometry, pulseMaterial));

      mount.dataset.backdropStatus = config.cyber
        ? "webgl-cyber-transfer"
        : "webgl-constellation";

      const resize = () => {
        width = window.innerWidth;
        height = window.innerHeight;
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.65));
        renderer.setSize(width, height, false);
        camera.aspect = width / Math.max(height, 1);
        camera.updateProjectionMatrix();
        fieldUniforms.uResolution.value.set(width, height);
      };

      const movePointer = (event: PointerEvent) => {
        targetPointer.set(
          event.clientX / Math.max(width, 1),
          1 - event.clientY / Math.max(height, 1),
        );
        hoverTarget = 1;
      };

      const leavePointer = () => {
        hoverTarget = 0;
      };

      const render = (time: number) => {
        const seconds = time * 0.001;
        pointer.lerp(targetPointer, 0.045);
        hoverStrength += (hoverTarget - hoverStrength) * 0.075;
        fieldUniforms.uTime.value = seconds;

        const pointerX = (pointer.x - 0.5) * 0.52;
        const pointerY = (pointer.y - 0.5) * 0.34;
        camera.position.x += (pointerX - camera.position.x) * 0.032;
        camera.position.y += (pointerY - camera.position.y) * 0.032;
        camera.lookAt(0, 0, 0);

        updateParticles(
          particles,
          particlePositions,
          seconds,
          pointer,
          hoverStrength,
          config,
        );
        particlePositionAttribute.needsUpdate = true;
        updateNodeSprites(
          nodeSprites,
          particlePositions,
          seconds,
          hoverStrength,
        );

        updateConnections(connections, connectionPositions, particlePositions);
        connectionPositionAttribute.needsUpdate = true;

        updatePulses(
          pulses,
          connections,
          pulsePositions,
          particlePositions,
          seconds,
          config,
        );
        pulsePositionAttribute.needsUpdate = true;

        connectionMaterial.opacity =
          config.connectionOpacity[0] +
          Math.sin(seconds * 0.36) * config.connectionOpacity[1];

        renderer.autoClear = true;
        renderer.render(fieldScene, fieldCamera);
        renderer.autoClear = false;
        renderer.clearDepth();
        renderer.render(scene, camera);
        renderer.autoClear = true;
        frame = window.requestAnimationFrame(render);
      };

      window.addEventListener("resize", resize);
      window.addEventListener("pointermove", movePointer, { passive: true });
      window.addEventListener("pointerleave", leavePointer);
      window.addEventListener("blur", leavePointer);
      frame = window.requestAnimationFrame(render);

      cleanupScene = () => {
        window.cancelAnimationFrame(frame);
        window.removeEventListener("resize", resize);
        window.removeEventListener("pointermove", movePointer);
        window.removeEventListener("pointerleave", leavePointer);
        window.removeEventListener("blur", leavePointer);
        for (const geometry of geometries) geometry.dispose();
        for (const material of materials) material.dispose();
        for (const texture of textures) texture.dispose();
        renderer.dispose();
      };
    };

    void startScene().catch(() => {
      mount.dataset.backdropStatus = "fallback";
    });

    return () => {
      disposed = true;
      cleanupScene?.();
    };
  }, [theme, variant]);

  return (
    <div
      className={`dynamic-ambient-backdrop is-${theme} ${variant === "public" ? "is-public" : ""}`}
      data-backdrop-theme={theme}
      data-backdrop-variant={variant}
      ref={mountRef}
      aria-hidden="true"
    >
      <span className="ambient-fallback one" />
      <span className="ambient-fallback two" />
      <canvas className="ambient-canvas" ref={canvasRef} />
    </div>
  );
}

function createParticles(count: number, config: BackdropConfig): Particle[] {
  const particles: Particle[] = [];
  for (let index = 0; index < count; index += 1) {
    const layer = seededValue(index + 19);
    const lane = Math.floor(seededValue(index + 23) * 13);
    const laneX = -5.2 + lane * (10.4 / 12);
    particles.push({
      x: config.cyber
        ? laneX + (seededValue(index + 1) - 0.5) * 0.18
        : seededValue(index + 1) * 10 - 5,
      y: config.cyber
        ? seededValue(index + 41) * 7.2 - 3.6
        : seededValue(index + 41) * 5.4 - 2.7,
      z: -1.4 + layer * 2.4,
      seed: seededValue(index + 83),
      size:
        config.particleSize[0] +
        seededValue(index + 137) * config.particleSize[1],
      drift: config.cyber
        ? 0.22 + seededValue(index + 173) * 0.42
        : 0.08 + seededValue(index + 173) * 0.12,
      phase: seededValue(index + 211) * Math.PI * 2,
    });
  }
  return particles;
}

function createConnections(
  particles: Particle[],
  maxDistance: number,
  config: BackdropConfig,
): Connection[] {
  const connections: Connection[] = [];
  const limit = Math.floor(particles.length * config.connectionLimitMultiplier);

  for (let index = 0; index < particles.length; index += 1) {
    const particle = particles[index];
    if (!particle) continue;
    const neighbors: Array<{ index: number; distance: number }> = [];

    for (
      let otherIndex = index + 1;
      otherIndex < particles.length;
      otherIndex += 1
    ) {
      const other = particles[otherIndex];
      if (!other) continue;
      const dx = particle.x - other.x;
      const dy = particle.y - other.y;
      const dz = (particle.z - other.z) * 0.42;
      const cyberLaneBias = config.cyber ? Math.abs(dx) * 1.55 : 0;
      const cyberVerticalBias = config.cyber ? Math.max(0, Math.abs(dy) - 1.45) * 0.55 : 0;
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz) + cyberLaneBias + cyberVerticalBias;
      if (distance < maxDistance)
        neighbors.push({ index: otherIndex, distance });
    }

    neighbors.sort((a, b) => a.distance - b.distance);
    for (const neighbor of neighbors.slice(0, config.cyber ? 3 : 2)) {
      if (connections.length >= limit) return connections;
      connections.push({ a: index, b: neighbor.index });
    }
  }

  return connections;
}

function createPulses(
  connections: Connection[],
  count: number,
  config: BackdropConfig,
): Pulse[] {
  const pulses: Pulse[] = [];
  if (connections.length === 0) return pulses;

  for (let index = 0; index < count; index += 1) {
    pulses.push({
      connectionIndex: Math.floor(
        seededValue(index + 307) * connections.length,
      ),
      seed: seededValue(index + 331),
      speed: config.cyber
        ? 0.18 + seededValue(index + 353) * 0.24
        : 0.045 + seededValue(index + 353) * 0.055,
      offset: seededValue(index + 379),
    });
  }

  return pulses;
}

function createNodeSprites({
  CanvasTexture,
  Sprite,
  SpriteMaterial,
  particles,
  config,
  materials,
  textures,
  additiveBlending,
}: {
  CanvasTexture: typeof import("three").CanvasTexture;
  Sprite: typeof import("three").Sprite;
  SpriteMaterial: typeof import("three").SpriteMaterial;
  particles: Particle[];
  config: BackdropConfig;
  materials: Disposable[];
  textures: Disposable[];
  additiveBlending: import("three").Blending;
}): NodeSprite[] {
  const canvas = document.createElement("canvas");
  canvas.width = 96;
  canvas.height = 96;
  const context = canvas.getContext("2d");
  if (context) {
    const gradient = context.createRadialGradient(48, 48, 0, 48, 48, 48);
    for (const [offset, color] of config.spriteStops) {
      gradient.addColorStop(offset, color);
    }
    context.fillStyle = gradient;
    context.fillRect(0, 0, 96, 96);
  }

  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;
  const material = new SpriteMaterial({
    map: texture,
    color: config.spriteColor,
    transparent: true,
    opacity: config.spriteOpacity,
    blending: additiveBlending,
    depthWrite: false,
    depthTest: false,
  });
  textures.push(texture);
  materials.push(material);

  return particles.map((particle) => {
    const sprite = new Sprite(material);
    sprite.renderOrder = 2;
    return {
      sprite,
      scale: config.spriteScale[0] + particle.size * config.spriteScale[1],
      seed: particle.seed,
    };
  });
}

function updateParticles(
  particles: Particle[],
  positions: Float32Array,
  seconds: number,
  pointer: import("three").Vector2,
  hoverStrength: number,
  config: BackdropConfig,
) {
  const pointerX = (pointer.x - 0.5) * 1.4;
  const pointerY = (pointer.y - 0.5) * 0.9;
  const pointerWorldX = (pointer.x - 0.5) * 12.2;
  const pointerWorldY = (pointer.y - 0.5) * 6.1;

  for (let index = 0; index < particles.length; index += 1) {
    const particle = particles[index];
    if (!particle) continue;
    const drift = seconds * particle.drift + particle.phase;
    const layer = (particle.z + 1.4) / 2.4;
    const influence = 0.025 + layer * 0.065;
    const offset = index * 3;
    if (config.cyber) {
      const stream = wrapUnit(
        (particle.y + 3.6) / 7.2 - seconds * particle.drift * 0.22,
      );
      const glitch =
        Math.sin(seconds * 5.4 + particle.seed * 80.0) *
        Math.sin(seconds * 1.7 + particle.seed * 17.0);
      const laneSnap =
        Math.round((particle.x + 5.2) / (10.4 / 12)) * (10.4 / 12) -
        5.2;
      const localX =
        laneSnap +
        Math.sin(seconds * 1.2 + particle.phase) * 0.055 +
        glitch * 0.025;
      const localY = stream * 7.2 - 3.6;
      const dx = localX - pointerWorldX;
      const dy = localY - pointerWorldY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const local = hoverStrength * Math.max(0, 1 - distance / (2.8 + layer * 0.7));
      positions[offset] =
        localX +
        Math.sin(seconds * 7.0 + particle.seed * 40.0) * local * 0.42;
      positions[offset + 1] =
        localY +
        Math.cos(seconds * 3.5 + particle.seed * 31.0) * local * 0.28;
      positions[offset + 2] =
        particle.z +
        Math.sin(seconds * 2.8 + particle.seed * 19.0) * 0.08 +
        local * 0.3;
      continue;
    }
    const baseX =
      particle.x +
      Math.sin(drift * 0.86 + particle.seed * 8.0) * 0.13 +
      pointerX * influence;
    const baseY =
      particle.y +
      Math.cos(drift * 1.08 + particle.seed * 11.0) * 0.1 +
      pointerY * influence;
    const dx = baseX - pointerWorldX;
    const dy = baseY - pointerWorldY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const radius = 2.15 + layer * 1.1;
    const local = hoverStrength * Math.max(0, 1 - distance / radius);
    const angle = Math.atan2(dy, dx) + (particle.seed - 0.5) * local * 0.95;
    const push = local * local * (1.25 + layer * 0.55);

    positions[offset] = baseX + Math.cos(angle) * push;
    positions[offset + 1] = baseY + Math.sin(angle) * push;
    positions[offset + 2] =
      particle.z +
      Math.sin(drift * 0.62 + particle.seed * 5.0) * 0.14 +
      local * 0.16;
  }
}

function updateNodeSprites(
  nodes: NodeSprite[],
  particlePositions: Float32Array,
  seconds: number,
  hoverStrength: number,
) {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (!node) continue;
    const offset = index * 3;
    node.sprite.position.set(
      particlePositions[offset] ?? 0,
      particlePositions[offset + 1] ?? 0,
      particlePositions[offset + 2] ?? 0,
    );
    const shimmer =
      1 +
      Math.sin(seconds * 0.9 + node.seed * 30) * 0.12 +
      hoverStrength * 0.08;
    node.sprite.scale.setScalar(node.scale * shimmer);
  }
}

function updateConnections(
  connections: Connection[],
  connectionPositions: Float32Array,
  particlePositions: Float32Array,
) {
  for (let index = 0; index < connections.length; index += 1) {
    const connection = connections[index];
    if (!connection) continue;
    const from = connection.a * 3;
    const to = connection.b * 3;
    const target = index * 6;
    connectionPositions[target] = particlePositions[from] ?? 0;
    connectionPositions[target + 1] = particlePositions[from + 1] ?? 0;
    connectionPositions[target + 2] = particlePositions[from + 2] ?? 0;
    connectionPositions[target + 3] = particlePositions[to] ?? 0;
    connectionPositions[target + 4] = particlePositions[to + 1] ?? 0;
    connectionPositions[target + 5] = particlePositions[to + 2] ?? 0;
  }
}

function updatePulses(
  pulses: Pulse[],
  connections: Connection[],
  pulsePositions: Float32Array,
  particlePositions: Float32Array,
  seconds: number,
  config: BackdropConfig,
) {
  for (let index = 0; index < pulses.length; index += 1) {
    const pulse = pulses[index];
    if (!pulse) continue;
    const connection = connections[pulse.connectionIndex];
    if (!connection) continue;
    const from = connection.a * 3;
    const to = connection.b * 3;
    const phase = wrapUnit(pulse.offset + seconds * pulse.speed);
    const eased = config.cyber
      ? Math.min(1, Math.floor(phase * 9) / 8 + Math.pow(phase, 3) * 0.08)
      : phase * phase * (3 - 2 * phase);
    const target = index * 3;
    pulsePositions[target] =
      (particlePositions[from] ?? 0) +
      ((particlePositions[to] ?? 0) - (particlePositions[from] ?? 0)) * eased;
    pulsePositions[target + 1] =
      (particlePositions[from + 1] ?? 0) +
      ((particlePositions[to + 1] ?? 0) - (particlePositions[from + 1] ?? 0)) *
        eased;
    pulsePositions[target + 2] =
      (particlePositions[from + 2] ?? 0) +
      ((particlePositions[to + 2] ?? 0) - (particlePositions[from + 2] ?? 0)) *
        eased +
      Math.sin(seconds * (config.cyber ? 5.2 : 0.8) + pulse.seed * 4) *
        (config.cyber ? 0.08 : 0.04);
  }
}
